import { AnimatePresence, motion } from "framer-motion";
import { Check } from "lucide-react";
import { useEffect, useRef } from "react";
import { ServiceCreationStep } from "./service-creation-types";

export const EASE_OUT_EXPO = [0.16, 1, 0.3, 1] as const;

export interface StepMeta {
  key: ServiceCreationStep;
  label: string;
}

export const STEPS_WITH_ENV: StepMeta[] = [
  { key: "select-type", label: "Choose service" },
  { key: "configure", label: "Configure" },
  { key: "env-vars", label: "Environment" },
];

export const STEPS_NO_ENV: StepMeta[] = [
  { key: "select-type", label: "Choose service" },
  { key: "configure", label: "Configure" },
];

interface StepBarProps {
  steps: StepMeta[];
  currentStep: ServiceCreationStep;
}

export function StepBar({ steps, currentStep }: StepBarProps) {
  const currentIdx = steps.findIndex((s) => s.key === currentStep);
  const prevIdxRef = useRef(currentIdx);

  useEffect(() => {
    prevIdxRef.current = currentIdx;
  });

  return (
    <div className="flex items-start justify-center">
      {steps.map((step, idx) => {
        const isDone = idx < currentIdx;
        const isCurrent = idx === currentIdx;

        return (
          <div key={step.key} className="flex items-start">
            <div className="flex flex-col items-center gap-2.5 w-32">
              <motion.div
                // key remount triggers the spring pop when a step becomes active.
                // Spring only supports 2 keyframes, so we go initial(0.75) → animate(1).
                key={`circle-${idx}-${isCurrent}`}
                aria-current={isCurrent ? "step" : undefined}
                initial={{ scale: isCurrent ? 0.75 : 1 }}
                animate={{
                  scale: 1,
                  backgroundColor:
                    isDone || isCurrent
                      ? "hsl(var(--primary))"
                      : "hsl(var(--background))",
                  borderColor:
                    isDone || isCurrent
                      ? "hsl(var(--primary))"
                      : "hsl(var(--border))",
                  color:
                    isDone || isCurrent
                      ? "hsl(var(--primary-foreground))"
                      : "hsl(var(--muted-foreground))",
                }}
                transition={{
                  scale: { type: "spring", stiffness: 500, damping: 18 },
                  backgroundColor: { type: "tween", duration: 0.25 },
                  borderColor: { type: "tween", duration: 0.25 },
                  color: { type: "tween", duration: 0.2 },
                }}
                className={[
                  "h-10 w-10 rounded-full flex items-center justify-center",
                  "text-sm font-bold border-2",
                  isCurrent ? "ring-4 ring-primary/15" : "",
                ].join(" ")}
              >
                <AnimatePresence mode="wait" initial={false}>
                  {isDone ? (
                    <motion.span
                      key="check"
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0, opacity: 0 }}
                      transition={{ type: "spring", stiffness: 500, damping: 20 }}
                    >
                      <Check className="h-4 w-4" />
                    </motion.span>
                  ) : (
                    <motion.span
                      key={`num-${idx}`}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.1 }}
                    >
                      {idx + 1}
                    </motion.span>
                  )}
                </AnimatePresence>
              </motion.div>

              <motion.span
                animate={{
                  color: isCurrent
                    ? "hsl(var(--foreground))"
                    : isDone
                    ? "hsl(var(--muted-foreground))"
                    : "hsl(var(--muted-foreground) / 0.4)",
                  fontWeight: isCurrent ? 600 : 400,
                }}
                transition={{ duration: 0.2 }}
                className="text-xs text-center leading-tight"
              >
                {step.label}
              </motion.span>
            </div>

            {idx < steps.length - 1 && (
              <div className="relative h-0.5 w-16 mt-5 shrink-0 rounded-full bg-border overflow-hidden">
                <motion.div
                  className="absolute inset-0 bg-primary origin-left rounded-full"
                  initial={false}
                  animate={{ scaleX: idx < currentIdx ? 1 : 0 }}
                  transition={{ duration: 0.3, ease: EASE_OUT_EXPO }}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
